import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Lock, Phone, Mail, AlertCircle } from "lucide-react";
import { PageHero, useSeo } from "../components/Shared";
import { Reveal } from "../components/Reveal";
import { COMPANY, FULFILLMENT_PORTAL_LOGIN_URL } from "../lib/site";

// Posts directly to the vendor's ExhibitForce login endpoint (form provided by vendor).
export default function FulfillmentPortal() {
  useSeo(
    "Inventory & Fulfillment Portal | MJG Promotions",
    "Secure client login for MJG fulfillment programs — inventory visibility, fulfillment requests, and shipment tracking."
  );
  const [params] = useSearchParams();
  const loginFailed = params.get("login") === "failed";
  const badRedir = `${window.location.origin}/client-portal/fulfillment?login=failed`;
  const inputCls =
    "mt-1.5 w-full border border-brand-line bg-white px-4 py-3 text-sm text-brand-black outline-none transition-colors focus:border-brand-red";

  return (
    <>
      <PageHero
        eyebrow="Client Access"
        title="Inventory & Fulfillment Portal"
        intro="Secure, account-based access to your stored inventory, fulfillment requests, and shipment activity."
      />
      <section className="bg-brand-warm py-16 sm:py-24" data-testid="fulfillment-portal-page">
        <div className="mx-auto max-w-lg px-5">
          <Reveal>
            <div className="border border-brand-line bg-white p-8 sm:p-10">
              <span className="flex h-12 w-12 items-center justify-center bg-brand-black text-white">
                <Lock className="h-6 w-6" aria-hidden="true" />
              </span>
              <h2 className="mt-6 text-xl font-extrabold tracking-tight text-brand-black sm:text-2xl">
                Sign in to your portal
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-brand-gray">
                Use the credentials provided by your MJG account manager.
              </p>

              {loginFailed && (
                <p
                  className="mt-5 flex items-start gap-2 border border-brand-red/30 bg-brand-red/5 px-4 py-3 text-sm font-semibold text-brand-red"
                  data-testid="fulfillment-login-error"
                  role="alert"
                >
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                  We couldn't sign you in. Please check your login and password, then try again.
                </p>
              )}

              <form
                action={FULFILLMENT_PORTAL_LOGIN_URL}
                method="post"
                name="mainform"
                className="mt-6"
                data-testid="fulfillment-login-form"
              >
                <label className="block text-sm font-bold text-brand-black" htmlFor="login">
                  Login
                  <input
                    name="login"
                    type="text"
                    id="login"
                    required
                    autoComplete="username"
                    className={inputCls}
                    data-testid="fulfillment-login-input"
                  />
                </label>
                <label className="mt-4 block text-sm font-bold text-brand-black" htmlFor="pwd">
                  Password
                  <input
                    name="pwd"
                    type="password"
                    id="pwd"
                    required
                    autoComplete="current-password"
                    className={inputCls}
                    data-testid="fulfillment-password-input"
                  />
                </label>
                <input type="hidden" name="badredir" value={badRedir} />
                <button
                  type="submit"
                  name="Submit"
                  value="Login"
                  className="mt-6 w-full bg-brand-red px-6 py-3.5 text-sm font-bold text-white transition-colors hover:bg-brand-redhover"
                  data-testid="fulfillment-login-submit"
                >
                  Login
                </button>
              </form>

              <div className="mt-7 border-t border-brand-line pt-5 text-sm text-brand-gray">
                <p className="font-semibold text-brand-black">Trouble signing in?</p>
                <div className="mt-2 flex flex-col gap-1.5">
                  <a href={COMPANY.phoneHref} data-testid="fulfillment-portal-call" className="inline-flex items-center gap-2 font-semibold text-brand-red hover:underline">
                    <Phone className="h-4 w-4" /> {COMPANY.phone}
                  </a>
                  <a href={COMPANY.emailHref} data-testid="fulfillment-portal-email" className="inline-flex items-center gap-2 font-semibold text-brand-red hover:underline">
                    <Mail className="h-4 w-4" /> {COMPANY.email}
                  </a>
                </div>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <Link
              to="/client-portal"
              data-testid="fulfillment-portal-back"
              className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-brand-gray transition-colors hover:text-brand-red"
            >
              <ArrowLeft className="h-4 w-4" /> Back to Client Portal
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
